/**
 * Shared helpers for outline `content.items` normalization.
 * The default parser matches server `Outline.safeJsonParse` (JSON.parse → jsonrepair
 * → extract substring) so malformed stringified payloads client-side behave like the API.
 */

import { jsonrepair } from "jsonrepair";

export function extractJsonSubstring(text) {
  if (typeof text !== "string") {
    return null;
  }
  const braceStart = text.indexOf("{");
  const braceEnd = text.lastIndexOf("}");
  if (braceStart !== -1 && braceEnd !== -1 && braceEnd > braceStart) {
    return text.slice(braceStart, braceEnd + 1);
  }
  const bracketStart = text.indexOf("[");
  const bracketEnd = text.lastIndexOf("]");
  if (bracketStart !== -1 && bracketEnd !== -1 && bracketEnd > bracketStart) {
    return text.slice(bracketStart, bracketEnd + 1);
  }
  return null;
}

export function safeJsonParseForOutline(text) {
  if (typeof text !== "string" || text.trim().length === 0) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    try {
      const repaired = jsonrepair(text);
      return JSON.parse(repaired);
    } catch {
      const fallback = extractJsonSubstring(text);
      if (fallback && fallback !== text) {
        try {
          return JSON.parse(fallback);
        } catch {
          return null;
        }
      }
      return null;
    }
  }
}

/**
 * Mirrors `Outline.normalizeItemsField`: arrays pass through; JSON strings (including
 * double-encoded) and `{ items: [...] }` payloads are unpacked. Returns null if unusable.
 *
 * @param {unknown} items
 * @param {(text: string) => unknown} [safeJsonParse]
 * @returns {unknown[] | null}
 */
export function normalizeOutlineItemsField(items, safeJsonParse = safeJsonParseForOutline) {
  if (Array.isArray(items)) {
    return items;
  }
  if (typeof items === "string") {
    let parsed = safeJsonParse(items);
    let attempts = 0;
    while (typeof parsed === "string" && attempts < 3) {
      attempts += 1;
      const reparsed = safeJsonParse(parsed);
      if (!reparsed || reparsed === parsed) {
        break;
      }
      parsed = reparsed;
    }
    if (Array.isArray(parsed)) {
      return parsed;
    }
    if (parsed && typeof parsed === "object" && parsed.items) {
      const nestedItems = normalizeOutlineItemsField(parsed.items, safeJsonParse);
      if (nestedItems) {
        return nestedItems;
      }
    }
  }
  return null;
}
